import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Delete, Check, X, Keyboard, ArrowUp, ArrowLeft, ArrowRight } from 'lucide-react';

/**
 * Le clavier de la caisse tactile.
 *
 * L'ecran de la vendeuse est tactile, mais l'appareil n'est pas reconnu comme
 * une tablette : le systeme n'ouvre donc aucun clavier quand elle touche un
 * champ. Et il n'y a pas de clavier physique. Elle ne pouvait rien saisir —
 * ni le poids pese, ni le montant recu, ni le nom d'un client, ni meme
 * chercher un produit.
 *
 * L'application apporte donc le sien. Il s'ouvre quand un champ est touche du
 * doigt, choisit sa disposition d'apres le champ — des chiffres pour un poids
 * ou un montant, des lettres pour un nom — et se referme quand on valide.
 *
 * Il n'est pas branche champ par champ : il ecoute le focus dans la page et
 * ecrit dans le champ actif. Un champ ajoute demain sera couvert sans qu'on y
 * pense, et les quinze champs de la caisse n'ont pas eu a etre modifies.
 *
 * Le clic a la souris ne l'ouvre jamais : sur un poste avec clavier, il ne
 * doit pas gener. C'est le doigt qui l'appelle.
 */

type Disposition = 'chiffres' | 'lettres' | 'symboles';

/** Ce que le dernier geste etait : le doigt appelle le clavier, la souris non. */
let dernierGeste: 'touch' | 'mouse' = 'mouse';

/**
 * Lu hors de ce fichier (ClassicLoginPage) pour decider, au focus, si un
 * champ marque `readOnly` par defaut doit le rester (doigt : le clavier
 * systeme ne doit pas s'ouvrir, celui-ci le remplace) ou s'ouvrir a la
 * frappe (souris/clavier physique : il n'y a alors aucun clavier systeme a
 * bloquer, et readOnly empecherait betement de taper).
 */
export const gesteEstTactile = () => dernierGeste === 'touch';

/**
 * La rangee des chiffres, au-dessus des lettres.
 *
 * Un nom de client, un numero de telephone, une adresse : on passe de l'un a
 * l'autre en pleine phrase. Basculer de disposition pour un seul chiffre
 * faisait perdre le fil — et le pave numerique, lui, ne sert que quand le
 * champ n'attend que des nombres.
 */
const RANGEE_CHIFFRES = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];

const TOUCHES_LETTRES: string[][] = [
  ['a', 'z', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
  ['q', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'm'],
  ['w', 'x', 'c', 'v', 'b', 'n', "'", '-'],
];

/**
 * Ce qui n'est ni lettre ni chiffre.
 *
 * Une adresse e-mail sans arobase, un mot de passe sans ponctuation : le
 * clavier ne permettait tout simplement pas de les ecrire.
 */
const TOUCHES_SYMBOLES: string[][] = [
  ['@', '.', ',', '/', ':', ';', '(', ')', '#', '%'],
  ['+', '-', '*', '=', '_', '"', '!', '?', '&', '€'],
  ['<', '>', '[', ']', '{', '}', '\\', '|', '~', '°'],
];

/** Les accents d'ici : Ouedraogo, Kabore, Traore s'ecrivent avec. */
const ACCENTS = ['é', 'è', 'ê', 'à', 'ç', 'ù', 'ô', 'î'];

const CHIFFRES: string[][] = [
  ['7', '8', '9'],
  ['4', '5', '6'],
  ['1', '2', '3'],
];

/**
 * Les types de champ qui n'attendent aucun texte tape.
 *
 * Une case a cocher reste un HTMLInputElement comme un champ de saisie — le
 * clavier ne le distinguait pas. Un doigt qui cochait une case sur Consulter
 * Stock recevait donc le clavier a la place, la case se voyait recouverte au
 * moment meme ou elle basculait. Ni elle, ni un bouton, ni un selecteur de
 * date natif n'ont de texte a taper : le clavier n'a rien a y faire.
 */
const TYPES_SANS_SAISIE = new Set([
  'checkbox',
  'radio',
  'button',
  'submit',
  'reset',
  'file',
  'range',
  'color',
  'image',
  'hidden',
  'date',
  'datetime-local',
  'month',
  'week',
  'time',
]);

/** Le champ attend-il des chiffres ? On le lit du champ, pas d'une liste. */
const dispositionDe = (el: HTMLInputElement): Disposition => {
  const type = (el.type || '').toLowerCase();
  const mode = (el.inputMode || '').toLowerCase();
  if (type === 'password' || type === 'email') return 'lettres';
  if (type === 'number' || type === 'tel') return 'chiffres';
  if (mode === 'numeric' || mode === 'decimal' || mode === 'tel') return 'chiffres';
  return 'lettres';
};

/** Un poids se saisit avec une virgule, et gagne des raccourcis. */
const estUnPoids = (el: HTMLInputElement) =>
  (el.inputMode || '').toLowerCase() === 'decimal' ||
  /poids/i.test(el.getAttribute('aria-label') || '') ||
  (el.step !== '' && el.step !== '1' && el.type === 'number');

/** Un montant : les billets courants evitent de taper chiffre par chiffre. */
const estUnMontant = (el: HTMLInputElement) =>
  /montant|recu|reçu|prix/i.test(
    `${el.getAttribute('aria-label') || ''} ${el.placeholder || ''} ${el.name || ''}`
  );

/**
 * Ecrire dans un champ que React controle.
 *
 * Poser `el.value` ne suffit pas : React ne voit rien et remet son ancienne
 * valeur au rendu suivant. On passe par le setter natif puis on emet
 * l'evenement `input`, ce que React ecoute.
 */
const ecrire = (el: HTMLInputElement, valeur: string) => {
  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
  if (setter) setter.call(el, valeur);
  else el.value = valeur;
  el.dispatchEvent(new Event('input', { bubbles: true }));
};

/**
 * Ou en est le curseur, quand le champ le permet.
 *
 * Un champ "number" (ou date, couleur...) refuse la selection — Chrome leve
 * une exception rien qu'a la lire. Ce n'est pas une erreur a signaler, juste
 * un type de champ qui n'a pas de curseur a suivre : le repli, la fin du
 * texte, revient au comportement d'avant.
 */
const positionCurseur = (el: HTMLInputElement): { debut: number; fin: number } => {
  try {
    return { debut: el.selectionStart ?? el.value.length, fin: el.selectionEnd ?? el.value.length };
  } catch {
    return { debut: el.value.length, fin: el.value.length };
  }
};

/**
 * Placer le curseur sans deplacer le texte.
 *
 * Ecrire une valeur remet toujours le curseur a la fin — c'est le
 * comportement natif d'un `value` pose par script. Sans ce rappel juste
 * apres, taper une lettre au milieu d'un mot la faisait sauter a la fin :
 * la vendeuse posait le doigt pour corriger une faute, et la faute
 * suivante retombait ailleurs.
 */
const placerCurseur = (el: HTMLInputElement, position: number) => {
  try {
    el.setSelectionRange(position, position);
  } catch {
    // Meme repli que ci-dessus : rien de plus a faire pour ce type de champ.
  }
};

export const ClavierTactile = ({
  actif = true,
}: {
  /** Coupe le clavier sans le retirer de la page. */
  actif?: boolean;
}) => {
  const [champ, setChamp] = useState<HTMLInputElement | null>(null);
  const [disposition, setDisposition] = useState<Disposition>('chiffres');
  const [majuscules, setMajuscules] = useState(true);
  const [valeur, setValeur] = useState('');
  const champRef = useRef<HTMLInputElement | null>(null);
  const panneauRef = useRef<HTMLDivElement | null>(null);

  // Le dernier geste : seul le doigt ouvre le clavier.
  useEffect(() => {
    const surGeste = (e: PointerEvent) => {
      dernierGeste = e.pointerType === 'touch' || e.pointerType === 'pen' ? 'touch' : 'mouse';
    };
    document.addEventListener('pointerdown', surGeste, true);
    return () => document.removeEventListener('pointerdown', surGeste, true);
  }, []);

  const fermer = useCallback(() => {
    champRef.current = null;
    setChamp(null);
  }, []);

  // Le focus d'un champ ouvre le clavier ; le focus d'autre chose le ferme.
  useEffect(() => {
    if (!actif) return;

    const surFocus = (e: FocusEvent) => {
      const cible = e.target as HTMLElement | null;
      if (!(cible instanceof HTMLInputElement) && !(cible instanceof HTMLTextAreaElement)) {
        return;
      }
      if (cible instanceof HTMLTextAreaElement) return;
      if (TYPES_SANS_SAISIE.has((cible.type || '').toLowerCase())) return;
      // `clavierForce` : readOnly n'y bloque que le clavier systeme (connexion
      // classique) — celui-ci doit au contraire s'en saisir, pas s'en detourner.
      if ((cible.readOnly && cible.dataset.clavierForce !== 'oui') || cible.disabled) return;
      if (cible.dataset.clavier === 'non') return;
      // Le doigt appelle le clavier ; la souris et le clavier physique non.
      if (dernierGeste !== 'touch') return;

      champRef.current = cible;
      setChamp(cible);
      setDisposition(dispositionDe(cible));
      setMajuscules(dispositionDe(cible) === 'lettres');
      setValeur(cible.value || '');

      // Le champ doit rester visible : le clavier occupe le bas de l'ecran.
      window.setTimeout(() => cible.scrollIntoView({ block: 'center', behavior: 'smooth' }), 60);
    };

    document.addEventListener('focusin', surFocus);
    return () => document.removeEventListener('focusin', surFocus);
  }, [actif, fermer]);

  // Le champ disparait (fenetre fermee, panier vide) : le clavier suit.
  useEffect(() => {
    if (!champ) return;
    const veille = window.setInterval(() => {
      if (!document.body.contains(champ)) fermer();
    }, 400);
    return () => window.clearInterval(veille);
  }, [champ, fermer]);

  /**
   * Faire de la place, y compris dans les fenetres qui ne defilent pas avec
   * la page.
   *
   * Le champ pousse par le clavier pouvait vivre dans une fenetre modale —
   * "position: fixed", centree dans l'ecran par elle-meme. Le reserver de
   * place ajoute plus bas sur la page ne la deplace pas : une fenetre fixe
   * reste a la meme hauteur quoi que fasse le reste du document.
   *
   * La hauteur reelle du clavier est donc posee sur la racine du document,
   * en variable CSS — mesuree, pas devinee, pour suivre les dispositions
   * (chiffres, lettres, symboles) qui n'ont pas la meme taille. Une regle
   * globale (index.css) l'applique ensuite a toute fenetre plein ecran
   * ("fixed inset-0", le patron commun a toutes les modales de
   * l'application) pendant que le clavier est ouvert : elle remonte
   * d'autant, sans que chaque fenetre ait eu a s'en soucier.
   *
   * En layout effect, pas en effect ordinaire : la variable doit etre posee
   * avant que le navigateur peigne, sinon l'espaceur plus bas (qui s'en sert
   * pour sa propre hauteur) affiche une image pour rien — clavier deja la,
   * place pas encore faite.
   */
  useLayoutEffect(() => {
    if (!champ) return; // ferme : le nettoyage du rendu precedent a deja tout remis a zero

    const racine = document.documentElement;
    racine.setAttribute('data-clavier-ouvert', 'true');

    const appliquerHauteur = () => {
      if (panneauRef.current) {
        racine.style.setProperty('--clavier-hauteur', `${panneauRef.current.offsetHeight}px`);
      }
    };
    appliquerHauteur();

    const observateur = new ResizeObserver(appliquerHauteur);
    if (panneauRef.current) observateur.observe(panneauRef.current);

    // Le champ remonte au-dessus, une fois la place faite autour de lui.
    const id = window.setTimeout(() => {
      champRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 80);

    return () => {
      observateur.disconnect();
      window.clearTimeout(id);
      racine.style.setProperty('--clavier-hauteur', '0px');
      racine.removeAttribute('data-clavier-ouvert');
    };
  }, [champ]);

  if (!actif || !champ) return null;

  /** Poser une valeur, et dire ou repart le curseur — a la fin si on ne precise rien. */
  const poser = (suite: string, curseur?: number) => {
    const el = champRef.current;
    if (!el) return;
    setValeur(suite);
    ecrire(el, suite);
    placerCurseur(el, curseur ?? suite.length);
  };

  /**
   * Une lettre s'insere au curseur, pas a la fin du texte.
   *
   * Elle a pu toucher le champ pour se remettre au milieu d'un mot — le
   * navigateur y place son curseur tout seul, sans rien demander au clavier.
   * Ignorer cette position et toujours ajouter a la fin aurait rendu ce
   * geste inutile : corriger une faute aurait tape la lettre suivante
   * ailleurs que prevu.
   */
  const taper = (touche: string) => {
    const el = champRef.current;
    if (!el) return;
    const t = disposition === 'lettres' && majuscules ? touche.toUpperCase() : touche;
    const { debut, fin } = positionCurseur(el);
    const suite = valeur.slice(0, debut) + t + valeur.slice(fin);
    poser(suite, debut + t.length);
    if (majuscules && disposition === 'lettres') setMajuscules(false);
  };

  /**
   * Une seule lettre, celle juste avant le curseur — ou la selection en
   * cours, s'il y en avait une. Jamais tout le champ : c'est le bouton
   * "Tout effacer", separe, qui fait ca.
   */
  const effacerUn = () => {
    const el = champRef.current;
    if (!el) return;
    const { debut, fin } = positionCurseur(el);
    if (fin > debut) {
      poser(valeur.slice(0, debut) + valeur.slice(fin), debut);
    } else if (debut > 0) {
      poser(valeur.slice(0, debut - 1) + valeur.slice(debut), debut - 1);
    }
  };

  const toutEffacer = () => poser('', 0);

  /** Deplacer le curseur d'une case, sans toucher au texte — les fleches. */
  const deplacerCurseur = (delta: number) => {
    const el = champRef.current;
    if (!el) return;
    const { debut } = positionCurseur(el);
    placerCurseur(el, Math.max(0, Math.min(valeur.length, debut + delta)));
  };

  const ajouter = (n: number) => {
    const actuel = parseFloat((valeur || '0').replace(',', '.')) || 0;
    const somme = Math.round((actuel + n) * 1000) / 1000;
    poser(String(somme).replace('.', ','));
  };

  const remplacer = (n: number) => poser(String(n));

  const poids = estUnPoids(champ);
  const montant = estUnMontant(champ);
  const nomDuChamp =
    champ.getAttribute('aria-label') || champ.placeholder || champ.name || 'Saisie';

  const Touche = ({
    children,
    onClick,
    large = false,
    ton = 'clair',
    label,
  }: {
    children: React.ReactNode;
    onClick: () => void;
    large?: boolean;
    ton?: 'clair' | 'sombre' | 'valider' | 'effacer';
    /** Pour les touches qui ne portent qu'une icone : rien a lire sans lui, au clavier ou a la voix. */
    label?: string;
  }) => (
    <button
      type="button"
      aria-label={label}
      // Le clavier ne doit pas voler le focus du champ : sans cela, le champ
      // perd son curseur au premier appui et la saisie s'arrete.
      onMouseDown={(e) => e.preventDefault()}
      onPointerDown={(e) => e.preventDefault()}
      onClick={onClick}
      // 60px de cote au minimum : une touche plus petite se rate au doigt.
      className={`min-h-[60px] min-w-[60px] md:min-h-[76px] md:text-2xl rounded-xl font-semibold text-lg flex items-center justify-center select-none active:scale-95 transition-transform ${
        large ? 'col-span-2' : ''
      } ${
        ton === 'valider'
          ? 'bg-emerald-600 text-white'
          : ton === 'effacer'
            ? 'bg-amber-100 text-amber-800'
            : ton === 'sombre'
              ? 'bg-neutral-200 text-neutral-800'
              : 'bg-white text-neutral-900 border border-neutral-200'
      }`}
    >
      {children}
    </button>
  );

  return (
    <>
      {/* Le clavier occupe le bas de l'ecran ; la page se decale d'autant
          pour que le champ en cours reste lisible — la meme variable que
          celle mesuree ci-dessous, jamais une hauteur devinee. */}
      <div style={{ height: 'var(--clavier-hauteur, 0px)' }} aria-hidden="true" />

      <div
        ref={panneauRef}
        // Plafonne a 45% de l'ecran : sur une tablette debout, un clavier
        // plus haut avalait le formulaire entier au-dessus de lui. Ce qui ne
        // tient plus dans ces 45% defile a l'interieur du clavier — jamais
        // par-dessus le champ.
        className="fixed bottom-0 left-0 right-0 z-[60] max-h-[55vh] md:max-h-[60vh] flex flex-col bg-neutral-100 border-t border-neutral-300 shadow-[0_-8px_24px_rgba(0,0,0,0.12)]"
      >
        {/* Ce qui est en train d'etre saisi, en grand : au doigt, on ne voit
            pas toujours le champ derriere sa main. Ne defile jamais avec le
            reste : c'est la seule ligne qu'il ne faut jamais perdre de vue. */}
        <div className="flex items-center gap-3 px-4 py-2.5 bg-white border-b border-neutral-200 flex-shrink-0">
          <Keyboard className="w-4 h-4 text-neutral-400 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-[11px] uppercase tracking-wide text-neutral-400 truncate">
              {nomDuChamp}
            </p>
            <p className="text-xl font-bold text-neutral-900 tabular-nums truncate">
              {valeur || <span className="text-neutral-300">—</span>}
            </p>
          </div>
          <button
            type="button"
            onClick={fermer}
            aria-label="Fermer le clavier"
            className="w-11 h-11 rounded-xl bg-neutral-100 flex items-center justify-center flex-shrink-0"
          >
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        <div className="p-2.5 overflow-y-auto flex-1 min-h-0">
          {disposition === 'chiffres' ? (
            <div className="max-w-md md:max-w-xl mx-auto space-y-2">
              {/* Les raccourcis : les billets courants pour un montant, les
                  demi-kilos pour un poids. */}
              {montant && (
                <div className="grid grid-cols-5 gap-2">
                  {[500, 1000, 2000, 5000, 10000].map((n) => (
                    <Touche key={n} onClick={() => remplacer(n)} ton="sombre">
                      <span className="text-sm tabular-nums">{n.toLocaleString('fr-FR')}</span>
                    </Touche>
                  ))}
                </div>
              )}
              {poids && (
                <div className="grid grid-cols-4 gap-2">
                  <Touche onClick={() => ajouter(0.5)} ton="sombre">
                    <span className="text-sm">+0,5</span>
                  </Touche>
                  <Touche onClick={() => ajouter(1)} ton="sombre">
                    <span className="text-sm">+1</span>
                  </Touche>
                  <Touche onClick={() => ajouter(5)} ton="sombre">
                    <span className="text-sm">+5</span>
                  </Touche>
                  <Touche onClick={() => ajouter(-1)} ton="sombre">
                    <span className="text-sm">−1</span>
                  </Touche>
                </div>
              )}

              <div className="grid grid-cols-4 gap-2">
                {CHIFFRES.flat().map((c) => (
                  <Touche key={c} onClick={() => taper(c)}>
                    {c}
                  </Touche>
                ))}
                <Touche onClick={effacerUn} ton="effacer" label="Supprimer un caractère">
                  <Delete className="w-5 h-5" />
                </Touche>
                <Touche onClick={toutEffacer} ton="effacer">
                  <span className="text-base">C</span>
                </Touche>
                <Touche onClick={() => taper('0')}>0</Touche>
                <Touche onClick={() => taper(poids ? ',' : '')}>
                  {poids ? ',' : <span className="text-neutral-300">·</span>}
                </Touche>
                <Touche onClick={() => deplacerCurseur(-1)} ton="sombre" label="Curseur vers la gauche">
                  <ArrowLeft className="w-5 h-5" />
                </Touche>
                <Touche onClick={() => deplacerCurseur(1)} ton="sombre" label="Curseur vers la droite">
                  <ArrowRight className="w-5 h-5" />
                </Touche>
                <Touche onClick={() => setDisposition('lettres')} ton="sombre">
                  <span className="text-sm">ABC</span>
                </Touche>
                <Touche onClick={fermer} ton="valider" label="Valider">
                  <Check className="w-5 h-5" />
                </Touche>
              </div>
            </div>
          ) : disposition === 'symboles' ? (
            <div className="max-w-3xl mx-auto space-y-2">
              {TOUCHES_SYMBOLES.map((rangee, i) => (
                <div
                  key={i}
                  className="grid gap-1.5"
                  style={{ gridTemplateColumns: `repeat(${rangee.length}, minmax(0, 1fr))` }}
                >
                  {rangee.map((t) => (
                    <Touche key={t} onClick={() => taper(t)}>
                      <span className="text-base">{t}</span>
                    </Touche>
                  ))}
                </div>
              ))}
              <div className="grid grid-cols-7 gap-1.5">
                <Touche onClick={() => setDisposition('lettres')} ton="sombre">
                  <span className="text-sm">ABC</span>
                </Touche>
                <Touche onClick={() => setDisposition('chiffres')} ton="sombre">
                  <span className="text-sm">123</span>
                </Touche>
                <Touche onClick={() => deplacerCurseur(-1)} ton="sombre" label="Curseur vers la gauche">
                  <ArrowLeft className="w-5 h-5" />
                </Touche>
                <Touche onClick={() => deplacerCurseur(1)} ton="sombre" label="Curseur vers la droite">
                  <ArrowRight className="w-5 h-5" />
                </Touche>
                <Touche onClick={effacerUn} ton="effacer" label="Supprimer un caractère">
                  <Delete className="w-5 h-5" />
                </Touche>
                {/* Separe de Delete : celle-ci efface tout le champ d'un coup, jamais au hasard d'un appui trop long. */}
                <Touche onClick={toutEffacer} ton="effacer">
                  <span className="text-xs">Tout</span>
                </Touche>
                <Touche onClick={fermer} ton="valider" label="Valider">
                  <Check className="w-5 h-5" />
                </Touche>
              </div>
              <div className="grid grid-cols-1">
                <Touche onClick={() => taper(' ')}>
                  <span className="text-sm text-neutral-500">espace</span>
                </Touche>
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto space-y-2">
              {/* Une adresse email revient toujours aux memes quatre signes
                  — les avoir sous la main evite d'aller les chercher parmi
                  tous les symboles pour chaque "@". */}
              {(champ.type === 'email' || (champ.inputMode || '').toLowerCase() === 'email') && (
                <div className="grid grid-cols-4 gap-1.5">
                  {['@', '.', '_', '-'].map((s) => (
                    <Touche key={s} onClick={() => taper(s)} ton="sombre">
                      <span className="text-base">{s}</span>
                    </Touche>
                  ))}
                </div>
              )}
              {/* Les chiffres au-dessus des lettres : on passe de l'un a
                  l'autre en pleine phrase, et changer de disposition pour un
                  seul chiffre faisait perdre le fil. */}
              <div className="grid grid-cols-10 gap-1.5">
                {RANGEE_CHIFFRES.map((c) => (
                  <Touche key={c} onClick={() => taper(c)}>
                    <span className="text-base">{c}</span>
                  </Touche>
                ))}
              </div>
              <div className="grid grid-cols-8 gap-1.5">
                {ACCENTS.map((a) => (
                  <Touche key={a} onClick={() => taper(a)} ton="sombre">
                    <span className="text-base">{majuscules ? a.toUpperCase() : a}</span>
                  </Touche>
                ))}
              </div>
              {TOUCHES_LETTRES.map((rangee, i) => (
                <div
                  key={i}
                  className="grid gap-1.5"
                  style={{ gridTemplateColumns: `repeat(${rangee.length}, minmax(0, 1fr))` }}
                >
                  {rangee.map((t) => (
                    <Touche key={t} onClick={() => taper(t)}>
                      <span className="text-base">{majuscules ? t.toUpperCase() : t}</span>
                    </Touche>
                  ))}
                </div>
              ))}
              <div className="grid grid-cols-8 gap-1.5">
                <Touche onClick={() => setMajuscules((m) => !m)} ton={majuscules ? 'sombre' : 'clair'} label="Majuscule">
                  <ArrowUp className="w-5 h-5" />
                </Touche>
                <Touche onClick={() => deplacerCurseur(-1)} ton="sombre" label="Curseur vers la gauche">
                  <ArrowLeft className="w-5 h-5" />
                </Touche>
                <Touche onClick={() => deplacerCurseur(1)} ton="sombre" label="Curseur vers la droite">
                  <ArrowRight className="w-5 h-5" />
                </Touche>
                <Touche onClick={() => setDisposition('symboles')} ton="sombre">
                  <span className="text-sm">?123</span>
                </Touche>
                <Touche onClick={() => setDisposition('chiffres')} ton="sombre">
                  <span className="text-sm">123</span>
                </Touche>
                <Touche onClick={effacerUn} ton="effacer" label="Supprimer un caractère">
                  <Delete className="w-5 h-5" />
                </Touche>
                {/* Separe de Delete : celle-ci efface tout le champ d'un coup, jamais au hasard d'un appui trop long. */}
                <Touche onClick={toutEffacer} ton="effacer">
                  <span className="text-xs">Tout</span>
                </Touche>
                <Touche onClick={fermer} ton="valider" label="Valider">
                  <Check className="w-5 h-5" />
                </Touche>
              </div>
              <div className="grid grid-cols-1">
                <Touche onClick={() => taper(' ')}>
                  <span className="text-sm text-neutral-500">espace</span>
                </Touche>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
